import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { DropdownMenu, useDropdownMenu } from 'src/components/DropdownMenu';
import { Appointment } from 'src/models/appointment/_types';
import { checkAppointmentEligibility } from 'src/models/appointment/appointment';
import { User } from 'src/models/user/_types';
import { getUserDisplayName } from 'src/models/user/utils';
import { Visit, VisitStatus } from 'src/models/visit/_types';
import { updateVisit } from 'src/models/visit/visit';
import { VisitStatusUpdateOptions } from 'src/shared/AppointmentEventCard/types';
import {
  CheckinBlockerReasonType,
  CheckinBlockerUser,
} from 'src/shared/CheckinBlocker/_types';
import toast from 'src/utils/toast';

import { VisitStatusButton } from './VisitStatusButton';
import { VisitStatusDropdownItems } from './VisitStatusDropdownItems';

interface Props {
  visit: Visit;
  appointment: Appointment;
  isTrainerView?: boolean;
  afterUpdateStatus?: (visit: Visit) => void;
  onUpdateVisitStatus?: (
    visit: Visit,
    status: VisitStatus,
    options?: VisitStatusUpdateOptions
  ) => void | Promise<void>;
  onStatusUpdateError?: (error: unknown, visit: Visit) => boolean;
  onEligibilityBlocked?: (args: { visit: Visit; users: CheckinBlockerUser[] }) => void;
}

export const VisitStatusDropdown: React.FC<Props> = ({
  visit,
  appointment,
  isTrainerView = true,
  afterUpdateStatus,
  onUpdateVisitStatus,
  onStatusUpdateError,
  onEligibilityBlocked,
}) => {
  const dropdownMenu = useDropdownMenu();
  const { t: tCheckinBlocker } = useTranslation('translation', {
    keyPrefix: 'checkinBlocker',
  });

  const updateVisitMutation = useMutation({
    mutationFn: (visit: Visit) => {
      return updateVisit(visit);
    },
    onSuccess: (updatedVisit) => {
      afterUpdateStatus?.(updatedVisit);
      toast.success('Visit(s) successfully updated!');
    },
    onError: (error, variables) => {
      if (onStatusUpdateError?.(error, variables)) return;
      toast.error('Visit could not be updated');
    },
  });

  const updateVisitStatus = async (
    visit: Visit,
    status: VisitStatus,
    additionalParams: VisitStatusUpdateOptions = {}
  ) => {
    if (onUpdateVisitStatus) {
      void onUpdateVisitStatus(visit, status, additionalParams);
      return;
    }
    updateVisitMutation.mutate({ ...visit, status, ...additionalParams });
    if (
      status === VisitStatus.Completed &&
      visit.appointmentId &&
      visit.userId &&
      onEligibilityBlocked
    ) {
      try {
        const eligibility = await checkAppointmentEligibility({
          appointment: { id: visit.appointmentId } as Appointment,
          user: { id: visit.userId } as User,
        });

        if (eligibility.reason || eligibility.trainerReason) {
          onEligibilityBlocked({
            visit,
            users: [
              {
                id: visit.userId?.toString() ?? '',
                name: getUserDisplayName(
                  visit.user ??
                    ({
                      firstName: visit.firstName,
                      lastName: visit.lastName,
                      email: visit.email,
                    } as User)
                ),
                profileImage: visit.user?.profileImage,
                blockers: [
                  {
                    type: (eligibility.reason ||
                      eligibility.trainerReason) as CheckinBlockerReasonType,
                    title: tCheckinBlocker('blockingReasons.custom.title') ?? '',
                    message: eligibility.reasonMessage,
                  },
                ],
              },
            ],
          });
          return;
        }
      } catch {
        toast.error(tCheckinBlocker('eligibilityCheckFailed'));
        return;
      }
    }

    await updateVisitMutation.mutateAsync({
      ...visit,
      status,
      ...additionalParams,
    });
  };

  return (
    <DropdownMenu
      open={dropdownMenu.isOpen}
      setOpen={dropdownMenu.setIsOpen}
      closeOnContentClick={false}
      trigger={
        <VisitStatusButton
          visit={visit}
          setOpen={dropdownMenu.setIsOpen}
          open={dropdownMenu.isOpen}
        />
      }
    >
      <VisitStatusDropdownItems
        visit={visit}
        appointment={appointment}
        isTrainerView={isTrainerView}
        setIsOpen={dropdownMenu.setIsOpen}
        updateVisitStatus={updateVisitStatus}
      />
    </DropdownMenu>
  );
};
